import { useCallback, useRef, useState } from 'react';

export enum STORAGE_KEYS {
  CHECKOUT_SUCCESS_DATA = 'checkoutSuccessData',
  JOURNEY_CHANGE_PAID_FULFILLMENT = 'journeyChangePaidFulfillment',
  KLIX_PAYMENT_PENDING = 'klixPaymentPending',
  PHONE_PREFIX_COUNTRY = 'phonePrefixCountry',
  TARA_RETURN_PATH = 'taraReturnPath',
  TOKEN_EXPIRY = 'tokenExpiry',
  TOKENS_REF = 'tokensRef',
}

export function useStorage<T>(
  storage: Storage,
  key: STORAGE_KEYS
): {
  value: T | null;
  setValue: (value: T | null) => void;
  removeValue: () => void;
};
export function useStorage<T>(
  storage: Storage,
  key: STORAGE_KEYS,
  initialValue: T
): { value: T; setValue: (value: T) => void; removeValue: () => void };
export function useStorage<T>(
  storage: Storage,
  key: STORAGE_KEYS,
  initialValue?: T
) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = storage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading storage key "${key}":`, error);
      return initialValue;
    }
  });

  // Mirrors the latest value so `setValue` stays referentially stable and
  // functional updates compute from the current value, while the storage
  // write happens synchronously at call time. The write must NOT live inside
  // the `setStoredValue` updater: React drops pending updates (updater never
  // runs) when the calling component unmounts in the same batch — e.g.
  // `setValue(...)` immediately followed by `navigate(...)`.
  const storedValueRef = useRef(storedValue);

  const setValue = useCallback(
    (value: T | ((prevValue: T) => T)) => {
      try {
        const valueToStore =
          value instanceof Function ? value(storedValueRef.current) : value;

        if (valueToStore === null) {
          storage.removeItem(key);
        } else {
          storage.setItem(key, JSON.stringify(valueToStore));
        }

        storedValueRef.current = valueToStore;
        setStoredValue(valueToStore);
      } catch (error) {
        console.error(`Error setting storage key "${key}":`, error);
      }
    },
    [key, storage]
  );

  const removeValue = useCallback(() => {
    try {
      storage.removeItem(key);
      storedValueRef.current = undefined as unknown as T;
      setStoredValue(undefined as unknown as T);
    } catch (error) {
      console.error(`Error removing storage key "${key}":`, error);
    }
  }, [key, storage]);

  return { value: storedValue, setValue, removeValue };
}
